import React, {useState} from "react";
import {SeriesMarker} from "./BrokerBenchmark";

/*
 * The broker's resident memory over a run, drawn under the latency chart of the same run: same
 * width, same time axis, same colour and marker per broker. Two charts rather than one with two
 * y-axes - latency and memory share nothing but time, and a second scale on one chart invites
 * reading a crossing of the lines as meaning something.
 *
 * The records carry it as a block of its own after the latency table, one row per latency interval
 * with the most the broker held during it:
 *
 *     Memory
 *     Interval        RSS
 *     0ms          412 Mb
 *     60000ms      431 Mb
 */

/**
 * Read one row of a record's memory block.
 * @return {interval, rssMb}, or null when the line is not one.
 */
export function parseMemoryRow(line)
{
    const m = line.match(/^(\d+)ms\s+([\d.]+)\s*(Mb|Gb)$/i);
    if (!m)
        return null;
    const value = parseFloat(m[2]);
    return {interval: parseInt(m[1], 10), rssMb: m[3].toLowerCase() === "gb" ? value * 1024 : value};
}

/** "412", "Mb" or "1.2", "Gb" as megabytes. */
export function rssToMb(value, unit)
{
    const number = parseFloat(value);
    return unit.toLowerCase() === "gb" ? number * 1024 : number;
}

/**
 * The average and the median of a broker's RSS over the intervals of a run, as the result files'
 * Average and Median rows give them; null when the run recorded no RSS.
 */
export function rssSummary(points)
{
    const mb = (points || []).map((p) => p.rssMb).sort((a, b) => a - b);
    if (mb.length === 0)
        return null;
    const middle = Math.floor(mb.length / 2);
    const median = mb.length % 2 ? mb[middle] : (mb[middle - 1] + mb[middle]) / 2;
    return {average: mb.reduce((sum, v) => sum + v, 0) / mb.length, median};
}

export function formatMemory(mb)
{
    if (mb >= 1024) {
        const gb = mb / 1024;
        return `${Number.isInteger(gb) ? gb : gb.toFixed(gb >= 10 ? 1 : 2)} Gb`;
    }
    return `${Math.round(mb)} Mb`;
}

function formatInterval(ms)
{
    return `${Math.round(ms / 1000)}s`;
}

/** A round axis top a little above the largest value: 1, 2 or 5 times a power of ten. */
function niceMax(value)
{
    if (value <= 0)
        return 1;
    const power = Math.pow(10, Math.floor(Math.log10(value)));
    for (const step of [1, 2, 5, 10]) {
        if (step * power >= value * 1.05)
            return step * power;
    }
    return 10 * power;
}

/**
 * @param series  [{key, label, color, shape, points: [{interval, rssMb}]}] - the brokers, in the
 *                order and colours of the latency chart above.
 * @param title   What the chart is, for the SVG title.
 */
/** The height of every chart on a test page, so a latency chart and the memory chart under it line up. */
export const CHART_HEIGHT = 300;

/** A chart's heading, above it. */
export function ChartTitle({children})
{
    return <h4 style={{margin: "18px 0 4px"}}>{children}</h4>;
}

/**
 * @param xFormat What an interval reads as on the x axis - elapsed time by default; the connection
 *                test passes the number of connections made by then.
 * @param xLabel  The x axis caption.
 * @param xTicks  Where to put the x axis labels - every other interval when not given.
 */
export function MemoryChart({series, title = "Broker memory (RSS) per interval", width = 760, height = CHART_HEIGHT,
                             xFormat = formatInterval, xLabel = "elapsed", xTicks: givenTicks = null})
{
    const [hover, setHover] = useState(null);

    const drawn = series.filter((s) => s.points && s.points.length > 0);
    if (!drawn.some((s) => s.points.length >= 2))
        return null;

    const margin = {top: 12, right: 16, bottom: 44, left: 96};
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const allPoints = drawn.flatMap((s) => s.points);
    const xMax = Math.max(...allPoints.map((p) => p.interval));
    const yMinRaw = Math.min(...allPoints.map((p) => p.rssMb));
    const yMaxRaw = Math.max(...allPoints.map((p) => p.rssMb));

    // Brokers in one scenario can hold 200 Mb and 30 Gb; on a linear axis the small ones lie on
    // the baseline and show nothing, so past a 50x spread the axis is logarithmic - as the latency
    // chart above it does. Otherwise linear and from zero: memory is a quantity, and an axis
    // starting above zero makes a few percent look like a leak.
    const useLog = yMinRaw > 0 && yMaxRaw / yMinRaw > 50;
    // Round in the unit the ticks are written in, so a 50 Gb top does not read as 48.8 Gb.
    // Log ticks at 1, 10 and 100 of a unit - 100 Mb, 1 Gb, 10 Gb - rather than at powers of ten
    // of megabytes, which read as 9.77 Gb.
    const logTicks = [1, 10, 100, 1024, 10240, 102400, 1048576];
    const yMax = useLog
        ? (logTicks.find((t) => t >= yMaxRaw) || logTicks[logTicks.length - 1])
        : (yMaxRaw >= 1024 ? niceMax(yMaxRaw / 1024) * 1024 : niceMax(yMaxRaw));
    const yMin = useLog ? ([...logTicks].reverse().find((t) => t <= yMinRaw) || 1) : 0;

    const xScale = (x) => margin.left + (xMax === 0 ? 0 : x / xMax) * innerW;
    const yScale = (y) => {
        const t = useLog
            ? (Math.log10(Math.max(y, yMin)) - Math.log10(yMin)) / (Math.log10(yMax) - Math.log10(yMin) || 1)
            : y / yMax;
        return margin.top + innerH - t * innerH;
    };

    const intervals = [...new Set(allPoints.map((p) => p.interval))].sort((a, b) => a - b);
    const xTicks = givenTicks || intervals.filter((_, i) => i % 2 === 0);
    const yTicks = [];
    if (useLog) {
        yTicks.push(...logTicks.filter((t) => t >= yMin && t <= yMax));
    } else {
        for (const f of [0, 0.25, 0.5, 0.75, 1])
            yTicks.push(f * yMax);
    }

    const onMove = (event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const px = ((event.clientX - rect.left) / rect.width) * width;
        if (px < margin.left || px > width - margin.right) { setHover(null); return; }
        const value = ((px - margin.left) / innerW) * xMax;
        let nearest = intervals[0];
        for (const i of intervals)
            if (Math.abs(i - value) < Math.abs(nearest - value)) nearest = i;
        setHover(nearest);
    };

    const hoverRows = hover === null ? [] : drawn
        .map((s) => ({...s, point: s.points.find((p) => p.interval === hover)}))
        .filter((r) => r.point);

    const tipW = 170;
    const tipH = 20 + hoverRows.length * 16;
    const tipX = hover === null ? 0 : Math.min(xScale(hover) + 10, width - margin.right - tipW);
    const tipY = margin.top + 4;

    return (
        <svg viewBox={`0 0 ${width} ${height}`}
             style={{width: "100%", maxWidth: width, height: "auto"}}
             onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
            <title>{title}</title>
            <text transform={`translate(16, ${margin.top + innerH / 2}) rotate(-90)`}
                  textAnchor="middle" fontSize="11" fill="#777">
                Memory (RSS)
            </text>

            {yTicks.map((t, i) => (
                <g key={`y${i}`}>
                    <line x1={margin.left} x2={width - margin.right} y1={yScale(t)} y2={yScale(t)} stroke="#eee"/>
                    <text x={margin.left - 8} y={yScale(t)} textAnchor="end" dominantBaseline="middle"
                          fontSize="11" fill="#555">{formatMemory(t)}</text>
                </g>
            ))}
            {xTicks.map((t, i) => (
                <text key={`x${i}`} x={xScale(t)} y={height - margin.bottom + 16} textAnchor="middle"
                      fontSize="11" fill="#555">{xFormat(t)}</text>
            ))}
            <text x={margin.left + innerW / 2} y={height - 6} textAnchor="middle" fontSize="11" fill="#777">
                {xLabel}
            </text>

            <line x1={margin.left} x2={width - margin.right} y1={height - margin.bottom}
                  y2={height - margin.bottom} stroke="#999"/>
            <line x1={margin.left} x2={margin.left} y1={margin.top} y2={height - margin.bottom} stroke="#999"/>

            {hover !== null && (
                <line x1={xScale(hover)} x2={xScale(hover)} y1={margin.top} y2={height - margin.bottom}
                      stroke="#bbb" strokeDasharray="3 3"/>
            )}

            {drawn.map((s) => (
                <polyline key={s.key} fill="none" stroke={s.color} strokeWidth="2"
                          points={s.points.map((p) => `${xScale(p.interval)},${yScale(p.rssMb)}`).join(" ")}/>
            ))}
            {drawn.map((s) => s.points.map((p, j) => (
                <SeriesMarker key={`${s.key}-${j}`} shape={s.shape}
                              cx={xScale(p.interval)} cy={yScale(p.rssMb)} fill={s.color}/>
            )))}

            {hover !== null && hoverRows.map((r) => (
                <SeriesMarker key={r.key} shape={r.shape} size={16} strokeWidth={2}
                              cx={xScale(hover)} cy={yScale(r.point.rssMb)} fill={r.color}/>
            ))}

            {hover !== null && hoverRows.length > 0 && (
                <g>
                    <rect x={tipX} y={tipY} width={tipW} height={tipH} rx="4" fill="#fff" stroke="#ccc" opacity="0.96"/>
                    <text x={tipX + 8} y={tipY + 14} fontSize="11" fill="#555">{xFormat(hover)} {xLabel}</text>
                    {hoverRows.map((r, i) => (
                        <g key={r.key}>
                            <SeriesMarker shape={r.shape} cx={tipX + 12} cy={tipY + 26 + i * 16}
                                          fill={r.color} stroke="none" strokeWidth={0}/>
                            <text x={tipX + 22} y={tipY + 30 + i * 16} fontSize="11" fill="#333">
                                {r.label} {formatMemory(r.point.rssMb)}
                            </text>
                        </g>
                    ))}
                </g>
            )}
        </svg>
    );
}
